import { MenuItemProps } from 'antd';
import { TriggeredComponentState } from '@/graphql/generated/graphql';
import { EmployeeCalendarShift } from './service/Api/types';

export interface MenuItem extends MenuItemProps {
  path: string;
  params?: string | null;
  participant?: boolean;
  admin?: boolean;
}

export interface LayoutSettings {
  title: { name: string; legalName: string };
  pieChartColors: string[];
  logo: string;
  darkLogo?: string;
  shortLogo?: string;
  signInLogo?: string;
}

export type ObjKeyT<T> = { [key: string]: T };

export type ActionT<T extends string, P> = {
  type: T;
  payload: P;
};

export type NotificationLinkType =
  | 'shifts'
  | 'openShifts'
  | 'capacity'
  | 'availability-chat'
  | 'availability-manual'
  | 'employee-compliance-agent'
  | 'employee-preferences-agent'
  | 'intake-queue';

export const enum PlanPreviewType {
  Duplicate = 'DUPLICATE',
  Preview = 'PREVIEW',
}

export type ConnectorDataT = Record<
  | 'sheetUrl'
  | 'sheetName'
  | 'userIdCol'
  | 'jobIdCol'
  | 'timestampCol'
  | 'serviceName'
  | 'isShiftData'
  | 'shiftStartCol'
  | 'shiftEndCol',
  string | undefined
>;

export const enum JobIDType {
  Manual = '__manual_',
  ClockIn = '__clock_in',
  ClockOut = '__clock_out',
  Referral = '__referral',
}

export const enum TransactionState {
  Processing = 'Processing',
  Accrued = 'Accrued',
  Overdue = 'Overdue',
  Paid = 'Paid',
  PartiallyPaid = 'Partially Paid',
  Denied = 'Denied',
}

export type FilterRenderOptionsT = {
  endDateHide: boolean;
  endDateLock: boolean;
  paymentStatusesHide: boolean;
  paymentStatusesLock: boolean;
  planComponentIdsHide: boolean;
  planComponentIdsLock: boolean;
  planIdsHide: boolean;
  planIdsLock: boolean;
  startDateHide: boolean;
  startDateLock: boolean;
};

export type FilterParametersT = {
  endDate: string[];
  paymentStatuses: TriggeredComponentState[];
  planComponentIds: string[];
  planIds: string[];
  startDate: string[];
} & FilterRenderOptionsT;

export enum ClockInClockOutViewStateT {
  CLOCKED_OUT = 'CLOCKED_OUT',
  WORKING = 'WORKING',
  TAKING_A_BREAK = 'TAKING_A_BREAK',
  SELECTING_LOCATION = 'SELECTING_LOCATION',
  THANK_YOU_ANIMATION = 'THANK_YOU_ANIMATION',
}

export const enum WidgetUI {
  Schedule = 'Schedule',
  Availability = 'Availability',
  ClockInClockOut = 'ClockInClockOut',
  Approval = 'Approval',
  Compliance = 'Compliance',
  RoutePlanner = 'RoutePlanner',
}

export const enum AgentPreferencetUI {
  Onboarding = 'Onboarding',
  Compliance = 'Compliance',
}

export const enum ShiftType {
  Shift = 'shift',
  Holiday = 'holiday',
  PTO = 'pto',
  Sick = 'sick',
  Unavailable = 'unavailable',
  Break = 'break',
  Other = 'other',
  TimeOff = 'timeoff',
  Oncall = 'oncall',
}

export const enum ShiftStatus {
  Filled = 'filled',
  InReview = 'in_review',
  NotYetReviewed = 'not_yet_reviewed',
  NoApplicants = 'no_applicants',
  AwaitingAcknowledgement = 'awaiting_acknowledgement',
}

export const enum ShiftRecurrence {
  Never = 'NEVER',
  Daily = 'DAILY',
  Weekly = 'WEEKLY',
  Monthly = 'MONTHLY',
  Yearly = 'YEARLY',
  Custom = 'CUSTOM',
}

export enum EnterManualTimeLoggingPopupState {
  REPORT_WORK = 'REPORT_WORK',
  REQUEST_SENT = 'REQUEST_SENT',
}

export enum UserActionType {
  REPORT_CLOCK_IN = 'REPORT_CLOCK_IN',
  REPORT_CLOCK_OUT = 'REPORT_CLOCK_OUT',
  REPORT_BREAK_START = 'REPORT_BREAK_START',
  REPORT_BREAK_END = 'REPORT_BREAK_END',
}

export enum TimekeepingStatus {
  InProgress = 'In Progress',
  Completed = 'Completed',
  InBreak = 'In Break',
}

export interface Schedule24HoursProps {
  employeeId: string | undefined;
}

export interface ShiftBlock {
  blockName: string;
  startTime: number;
  endTime: number;
  shifts: Shift[];
}

export interface Shift {
  startTime: number;
  endTime: number;
  firstName: string;
  lastName: string;
}

export interface GreenOverlayProps {
  rollingSchedule: ShiftBlock[];
  divRefHeights: (number | undefined)[];
  scheduleWidth: number;
}

export interface CustomerData {
  address: string | null;
  encoded_address: string | null;
  first_name: string | null;
  id: string | null;
  last_name: string | null;
  metadata: string | null;
}

export interface State {
  abbreviation?: string | null | undefined;
  id: string;
  name: string;
}

export interface NextShift {
  customer: CustomerData;
  endTime: string;
  entityId: string;
  entityName: string;
  id: string;
  licensedStates: State[] | null;
}

export type TimerProps = {
  nextShift: NextShift;
  setCountdownOver: React.Dispatch<React.SetStateAction<boolean>>;
};

export interface ShiftsPanelProps {
  title: string;
  queryData: EmployeeCalendarShift[];
}

export enum GenericJobSubType {
  Clinical = 'Clinical',
  Collaboration = 'Collaboration',
  NonPortal = 'Non-portal',
  Standard = 'Standard',
}

export enum SteadyMDJobSubType {
  Nucleus = 'Nucleus',
  HealthOme = 'HealthOme',
  ChartReview = 'Chart Review',
  NoomChartReview = 'Noom Chart Review',
  ClinicalInterviews = 'Clinical Interviews',
  NoomAdmin = 'Noom Admin',
  NeuroQure = 'NeuroQure',
}

export type CustomerJobSubTypes = {
  [key: string]: string[];
};

export const enum CalendarSyncConnectorType {
  Google = 'GOOGLE',
  iCal = 'ICAL',
  Outlook = 'OUTLOOK',
  Manual = 'MANUAL',
}

export type LinkJWTEncodedT = {
  type: NotificationLinkType;
  orgId: string;
  userId: string;
  employeeId: string;
  userEmail: string;
  data: {
    id: string;
    resource: string;
    type: string;
    capacityRequestId: string;
    patientId?: string | null;
    entityId?: string | null;
    startTime?: string | null;
    endTime?: string | null;
  };
  firstName: string;
};

export enum DocumentStatus {
  approved = 'APPROVED',
  pending = 'PENDING',
  rejected = 'REJECTED',
  expired = 'EXPIRED',
  renewed = 'RENEWED',
  active = 'ACTIVE',
}
